import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';

export const useTaskFormState = (task) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: null,
    assignee_id: null,
    status_id: null,
    due_date: null,
  });
  const [projects, setProjects] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [assignableEmployees, setAssignableEmployees] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [isFetchingRelatedData, setIsFetchingRelatedData] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (task) {
      setFormData({
        name: task.name || '',
        description: task.description || '',
        project_id: task.project_id || null,
        assignee_id: task.assignee_id || null,
        status_id: task.status_id || null,
        due_date: task.due_date || null,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        project_id: null,
        assignee_id: null,
        status_id: null,
        due_date: null,
      });
    }
  }, [task]);

  const fetchInitialRelatedData = useCallback(async () => {
    setIsFetchingRelatedData(true);
    try {
      const [projectsRes, employeesRes, statusesRes] = await Promise.all([
        supabase.from('projects').select('id, name'),
        supabase.from('employees').select('id, name, team_id'),
        supabase.from('custom_statuses').select('id, name'),
      ]);

      if (projectsRes.error) throw projectsRes.error;
      if (employeesRes.error) throw employeesRes.error;
      if (statusesRes.error) throw statusesRes.error;

      setProjects(projectsRes.data || []);
      const employeesData = employeesRes.data || [];
      setAllEmployees(employeesData);
      setStatuses(statusesRes.data || []);
      setAssignableEmployees(employeesData); 

    } catch (error) {
      console.error('Error fetching related data for task form:', error);
      toast({
        title: 'Error fetching data',
        description: 'Could not load projects, employees, or statuses.',
        variant: 'destructive',
      });
    }
    setIsFetchingRelatedData(false);
  }, [toast]);

  useEffect(() => {
    fetchInitialRelatedData();
  }, [fetchInitialRelatedData]);

  useEffect(() => {
    let currentAssignable = allEmployees;
    if (formData.project_id) {
        currentAssignable = allEmployees; 
    }
    setAssignableEmployees(currentAssignable);
    
    const isCurrentAssigneeInList = currentAssignable.some(emp => emp.id === formData.assignee_id);

    if (formData.assignee_id && !isCurrentAssigneeInList) {
      // If the task is being edited and its original project_id matches the current formData.project_id,
      // but the assignee is somehow not in the list (e.g. employee deleted), then clear it.
      // Or, if the project_id has changed and the old assignee is not in the new list.
      if (task && task.project_id === formData.project_id && task.assignee_id === formData.assignee_id) {
        // This case means the original assignee for the *same* project is no longer valid.
        // This might happen if an employee is removed from the system/team.
        // setFormData(prev => ({ ...prev, assignee_id: null })); // Potentially clear
      } else if (!task || (task && task.project_id !== formData.project_id)) {
        // Project changed or it's a new task, and selected assignee is not in the (potentially filtered) list.
        setFormData(prev => ({ ...prev, assignee_id: null }));
      }
    }

  }, [formData.project_id, allEmployees, task, projects]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    const processedValue = value === '' || value === undefined ? null : value;
    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData, [name]: processedValue };
      return newFormData;
    });
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({ ...prev, [name]: date ? new Date(date).toISOString() : null }));
  };

  return {
    formData,
    setFormData,
    projects,
    assignableEmployees,
    statuses,
    isFetchingRelatedData,
    handleChange,
    handleSelectChange,
    handleDateChange,
  };
};